import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { CustomerVaultsApi } from "@/openapi/generated";
import type { GetCustomerVaultResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { normalizeVaultDefaults, vaultIdsEqual } from "@/features/balance/utils/vault";

interface UseVaultSetDefaultParams {
  userId: number;
  vaultId: number;
  tenantId: number;
}

export const useVaultSetDefault = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, isError, error } =
    useMutation({
      mutationFn: async ({
        userId,
        vaultId,
        tenantId,
      }: UseVaultSetDefaultParams) => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }

        const service = createAuthenticatedApi(
          CustomerVaultsApi,
          accessToken,
        );

        await service.apiUsersUserIdVaultsVaultIdDefaultPut({
          userId,
          vaultId,
          xTenantID: tenantId,
        });

        return { userId, vaultId };
      },
      onMutate: async ({ userId, vaultId }) => {
        await queryClient.cancelQueries({
          queryKey: [QueryKeys.CustomerVaults, userId],
        });

        const previous = queryClient.getQueryData<GetCustomerVaultResponse[]>([
          QueryKeys.CustomerVaults,
          userId,
        ]);

        if (previous) {
          queryClient.setQueryData<GetCustomerVaultResponse[]>(
            [QueryKeys.CustomerVaults, userId],
            normalizeVaultDefaults(
              previous.map((vault) => ({
                ...vault,
                isDefault: vaultIdsEqual(vault.id, vaultId),
              })),
            ),
          );
        }

        return { previous };
      },
      onError: (_error, { userId }, context) => {
        if (context?.previous) {
          queryClient.setQueryData(
            [QueryKeys.CustomerVaults, userId],
            context.previous,
          );
        }
      },
      onSettled: (_data, _error, { userId }) => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.CustomerVaults, userId],
        });
      },
    });

  return { mutate, mutateAsync, isPending, isSuccess, isError, error };
};
